/**
 * CommunityFeed — Social feed tab for a community.
 *
 * Displays user-shared content (links, videos, chronicles) and provides
 * a Share button that opens FeedShareModal.
 *
 * Data flow:
 *   - Uses fetchNewsFeed() with contentType=community_share filter
 *   - Share modal submits via /api/News/share
 *   - Items appear immediately (auto-approved)
 *   - For chronicle items, resolves the full original image from the
 *     chronicle API (heroOriginalUrl) to avoid showing the cropped banner.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { fetchNewsFeed, removeFeedItem, blacklistDomainFromFeed, type NewsModerationItem, type NewsArticle } from '../../api/newsApi';
import { fetchChronicle } from '../../api/chronicles';
import { mediaSrc } from '../../api/mediaUrl';
import FeedImage from '../FeedImage/FeedImage';
import { FeedComposer } from '../Feed/FeedComposer';
import { FeedCard, type FeedCardMenuAction } from '../Feed/FeedCard';
import VoiceMomentRibbon, { VOICE_GLOW_CLASS } from '../VoiceMomentRibbon/VoiceMomentRibbon';
import ChroniclePickerModal from '../ChroniclePickerModal/ChroniclePickerModal';
import { FeedActionBar } from '../Comments/FeedActionBar';
import { useAuth } from '../../auth/AuthContext';
import styles from './CommunityFeed.module.css';
import { sanitizeEmbed } from '../../utils/sanitizeHtml';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  communityGuid: string;
  communityName: string;
  users?: Record<string, any>[];
  currentUserGuid?: string;
  isSystemAdmin?: boolean;
  /** Whether the viewer may post to this feed (community membership +
   *  voice-ladder gate — see CommunityDetailPage.canPostFeed). */
  canPost?: boolean;
  /** Voice-ladder (2026-07-07): shown in place of the composer for members
   *  who haven't earned posting here yet — the visible earning path. */
  postingLockedHint?: string;
  /** Navigate to a sibling tab on the community detail page (e.g. 'news'). */
  onNavigateToTab?: (tab: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Relative time — uses Intl.RelativeTimeFormat for locale-aware output. */
function timeSince(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
    if (diffH < 1) return rtf.format(0, 'hour'); // "this hour" / "now" depending on locale
    if (diffH < 24) return rtf.format(-diffH, 'hour');
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return rtf.format(-diffD, 'day');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

function getShareIcon(article: NewsArticle): string {
  if (article.chronicleGuid) return '📋';
  if (article.embedType === 'video') return '▶️';
  return '🔗';
}

/** Check whether a moderation item is an auto-posted featured news article. */
function isFeaturedNews(item: NewsModerationItem): boolean {
  return item.contentType === 'news_featured';
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const CommunityFeed: React.FC<Props> = ({ communityGuid, communityName, users, currentUserGuid, isSystemAdmin, canPost, postingLockedHint, onNavigateToTab }) => {
  const { t } = useAppTranslation('pages');
  const navigate = useNavigate();
  const { user } = useAuth();
  // Open-feed comments are 18+ only; minors still see posts + reactions.
  const isAdult = !!user?.isAdult;

  /** Resolve a user GUID to a display name. */
  const resolveUserName = useCallback((guid?: string): string => {
    if (!guid || !users) return '';
    const upper = String(guid).toUpperCase();
    const u = users.find((x) => String(x.user_guid || '').toUpperCase() === upper);
    return u?.display_name || u?.displayName || '';
  }, [users]);

  /** Resolve a user GUID to their profile photo (feed avatars were falling
   *  back to initials for EVERY poster because the card model never carried
   *  an avatar — Skip, 2026-07-08). FeedCard runs the value through
   *  mediaSrc(), so raw storage keys are fine here. */
  const resolveUserAvatar = useCallback((guid?: string): string | null => {
    if (!guid || !users) return null;
    const upper = String(guid).toUpperCase();
    const u = users.find((x) => String(x.user_guid || '').toUpperCase() === upper);
    return u?.profile_image_url || u?.profileImageUrl || null;
  }, [users]);
  const [items, setItems] = useState<NewsModerationItem[]>([]);
  const [loading, setLoading] = useState(true);
  // DEV-1441 — first-post Voice moment (ribbon + one-shot glow on the
  // newest card). Set when a share response carries firstPost: true.
  const [voiceMoment, setVoiceMoment] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  /** Chronicle picker state — which feed item is being shared to a chronicle. */
  const [chroniclePickerItem, setChroniclePickerItem] = useState<NewsModerationItem | null>(null);

  /**
   * Map of chronicleGuid → full original image URL.
   * Populated after feed loads by fetching each linked chronicle.
   * The feed renders from this map instead of the baked news_items.image_url.
   */
  const [chronicleImages, setChronicleImages] = useState<Record<string, string>>({});

  /** Handle feed item clicks — chronicles use in-app navigation, external links open in new tab. */
  const handleItemClick = useCallback((article: NewsArticle) => {
    if (article.chronicleGuid) {
      navigate(`/chronicles/${article.chronicleGuid}/view`, {
        state: { from: 'feed', communityGuid, communityName },
      });
      return;
    }
    // External links and videos open in a new tab
    if (article.canonicalUrl) {
      window.open(article.canonicalUrl, '_blank', 'noopener,noreferrer');
    }
  }, [navigate, communityGuid, communityName]);

  // ── Load shared feed items ──────────────────────────────
  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      // Request both community shares and auto-posted featured news.
      // The backend supports comma-separated contentType values after
      // the GetFeed update.  If running on an older backend build, the
      // comma string won't match any rows — fall back to community_share only.
      let feed = await fetchNewsFeed(communityGuid, {
        count: 50,
        contentType: 'community_share,news_featured',
      });
      if (feed.length === 0) {
        // Fallback: old backend treats the comma string as a single
        // value and returns nothing.  Retry with the original filter.
        feed = await fetchNewsFeed(communityGuid, {
          count: 50,
          contentType: 'community_share',
        });
      }
      // DEV-989 — Dedupe by underlying article. The same news_item can land
      // in the feed twice: once as a `news_featured` companion (auto-posted
      // when an article is featured on the News tab) and once as a
      // `community_share` (user-shared via the chronicle picker). Prefer the
      // news_featured presentation when both exist, since that's the
      // editorially-promoted version with the orange FEATURED badge.
      const seen = new Map<string, NewsModerationItem>();
      for (const item of feed) {
        const key = item.article?.newsItemGuid
                    || item.article?.canonicalUrl
                    || item.moderationGuid;
        if (!key) continue;
        const existing = seen.get(key);
        if (!existing) {
          seen.set(key, item);
        } else if (
          item.contentType === 'news_featured'
          && existing.contentType !== 'news_featured'
        ) {
          // Promote: the featured version wins over an earlier community_share.
          seen.set(key, item);
        }
        // Otherwise keep existing (first-wins for same-priority duplicates).
      }
      const deduped = Array.from(seen.values());
      setItems(deduped);

      // For chronicle-linked items, fetch the full original image from the chronicle API.
      // This runs client-side so it works regardless of whether the backend has been rebuilt.
      const chronicleGuids = feed
        .map((m) => m.article?.chronicleGuid)
        .filter((g): g is string => !!g);
      const uniqueGuids = [...new Set(chronicleGuids)];

      if (uniqueGuids.length > 0) {
        const imageMap: Record<string, string> = {};
        await Promise.all(
          uniqueGuids.map(async (cGuid) => {
            try {
              const c = await fetchChronicle(cGuid);
              const url = c.heroOriginalUrl || c.heroImageUrl;
              if (url) imageMap[cGuid] = url;
            } catch {
              // Chronicle may have been deleted — ignore
            }
          })
        );
        setChronicleImages(imageMap);
      }
    } catch {
      // Feed endpoint may return empty
    } finally {
      setLoading(false);
    }
  }, [communityGuid]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  // Close menu on outside click
  useEffect(() => {
    if (!openMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openMenu]);

  /** Remove a single item from this community's feed. */
  const handleRemove = useCallback(async (moderationGuid: string) => {
    setOpenMenu(null);
    try {
      await removeFeedItem(communityGuid, moderationGuid);
      setItems((prev) => prev.filter((i) => i.moderationGuid !== moderationGuid));
    } catch {
      // Silently fail — item may already have been removed
    }
  }, [communityGuid]);

  /** Domains that must never be blacklisted — platform's own domains. */
  const isProtectedDomain = useCallback((item: NewsModerationItem): boolean => {
    const domain = item.article?.sourceDomain || item.article?.ogSiteName || '';
    const url = item.article?.canonicalUrl || '';
    let host = domain.toLowerCase();
    if (!host && url) {
      try { host = new URL(url).hostname.toLowerCase(); } catch { /* ignore */ }
    }
    if (!host) return false;
    // Protect Life4Earth and its subdomains
    const stripped = host.replace(/^www\./, '');
    return stripped === 'life4earth.org'
      || stripped.endsWith('.life4earth.org')
      || stripped === 'life4earth.com'
      || stripped.endsWith('.life4earth.com')
      || item.article?.contentType === 'user_blog';
  }, []);

  /** Blacklist the domain of a feed item — system admin only. */
  const handleBlacklistDomain = useCallback(async (item: NewsModerationItem) => {
    setOpenMenu(null);
    const domain = item.article?.sourceDomain || item.article?.ogSiteName || '';
    const url = item.article?.canonicalUrl || '';
    // Extract domain from URL if sourceDomain not available
    let displayDomain = domain;
    if (!displayDomain && url) {
      try { displayDomain = new URL(url).hostname; } catch { /* ignore */ }
    }
    const confirmMsg = displayDomain
      ? t('communityDetail.feedBlacklistConfirm', { domain: displayDomain })
      : t('communityDetail.feedBlacklistConfirmGeneric');
    if (!window.confirm(confirmMsg)) return;
    try {
      await blacklistDomainFromFeed({
        moderationGuid: item.moderationGuid,
        scopeGuid: communityGuid,
      });
      setItems((prev) => prev.filter((i) => i.moderationGuid !== item.moderationGuid));
    } catch {
      // Silently fail
    }
  }, [communityGuid, t]);

  /** Resolve the best image URL for a feed article.
   *  For chronicles: use the full original from the chronicle API.
   *  For everything else: use the baked imageUrl / ogImageUrl. */
  const resolveImageUrl = useCallback((article: NewsArticle): string | undefined => {
    if (article.chronicleGuid && chronicleImages[article.chronicleGuid]) {
      return mediaSrc(chronicleImages[article.chronicleGuid]) ?? undefined;
    }
    return mediaSrc(article.imageUrl || article.ogImageUrl) ?? undefined;
  }, [chronicleImages]);

  return (
    <div className={styles.container}>
      {/* Composer (voice-ladder gated) — replaces the old "Community Feed" header + Share button. */}
      {canPost && (
        <FeedComposer
          communityGuid={communityGuid}
          onShared={(firstPost) => {
            loadFeed();
            // DEV-1441 Voice moment — first-ever share, once per lifetime.
            if (firstPost) setVoiceMoment(true);
          }}
        />
      )}

      {/* DEV-1441 — one-time first-post Voice ribbon (variant B). */}
      <VoiceMomentRibbon open={voiceMoment} onDismiss={() => setVoiceMoment(false)} />
      {/* Members without voice here yet see the earning path, not a void. */}
      {!canPost && postingLockedHint && (
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          {postingLockedHint}
        </p>
      )}

      {/* Loading */}
      {loading && (
        <div className={styles.loading}>{t('communityDetail.news.loading')}</div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>💬</div>
          <h3 className={styles.emptyTitle}>{t('communityDetail.feedEmptyTitle')}</h3>
          <p className={styles.emptyHint}>{t('communityDetail.feedEmptyHint')}</p>
        </div>
      )}

      {/* Feed items */}
      {!loading && items.length > 0 && (
        <div className={styles.feedList}>
          {items.map((item, itemIdx) => {
            const a = item.article;
            if (!a) return null;

            /* ── Featured news card ────────────────────────── */
            if (isFeaturedNews(item)) {
              const heroImg = resolveImageUrl(a);
              return (
                <article
                  key={item.moderationGuid}
                  className={styles.featuredPost}
                  onClick={() => handleItemClick(a)}
                >
                  {/* Header: newspaper avatar + attribution + badge */}
                  <div className={styles.featuredHeader}>
                    <span className={styles.newsAvatar}>📰</span>
                    <span className={styles.featuredAttribution}>
                      {t('communityDetail.feedSharedByCommunityNews', { name: communityName })}
                    </span>
                    <span className={styles.featuredBadge}>
                      {t('communityDetail.feedFeaturedBadge')}
                    </span>
                    <span className={styles.feedItemTime}>{timeSince(a.publishedAt)}</span>
                    {/* Curator actions (reuse existing menu) */}
                    <div className={styles.feedItemActions} ref={openMenu === item.moderationGuid ? menuRef : undefined}>
                      <button
                        type="button"
                        className={styles.menuTrigger}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenu(openMenu === item.moderationGuid ? null : item.moderationGuid);
                        }}
                        aria-label={t('communityDetail.feedItemMenu')}
                      >
                        ⋯
                      </button>
                      {openMenu === item.moderationGuid && (
                        <div className={styles.menuDropdown}>
                          <button
                            type="button"
                            className={styles.menuItemDanger}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(item.moderationGuid);
                            }}
                          >
                            {t('communityDetail.feedRemoveFromCommunity', { name: communityName })}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Hero image — collapses if the source URL is dead (DEV-1420) */}
                  {heroImg && (
                    <FeedImage className={styles.featuredImage} src={heroImg} />
                  )}

                  {/* Body: source, title, excerpt */}
                  <div className={styles.featuredBody}>
                    <div className={styles.featuredSource}>
                      {(a.sourceName || a.sourceDomain || a.ogSiteName || '').toUpperCase()}
                    </div>
                    <h3 className={styles.featuredTitle}>{a.title || a.ogTitle || a.canonicalUrl}</h3>
                    {(a.excerpt || a.ogDescription) && (
                      <p className={styles.featuredExcerpt}>
                        {(a.excerpt || a.ogDescription || '').slice(0, 200)}
                      </p>
                    )}
                  </div>

                  {/* Footer: CTA (share lives in the action bar below) */}
                  <div className={styles.featuredFooter}>
                    <button
                      type="button"
                      className={styles.featuredCta}
                      onClick={(e) => {
                        e.stopPropagation();
                        onNavigateToTab?.('news');
                      }}
                    >
                      {t('communityDetail.feedReadMoreNews', { name: communityName })}
                    </button>
                  </div>

                  {isAdult && (a.chronicleGuid || a.newsItemGuid) && (
                    <FeedActionBar
                      anchorType={a.chronicleGuid ? 'chronicle' : 'news_item'}
                      anchorId={a.chronicleGuid || a.newsItemGuid}
                      post={{
                        authorName: a.sourceName || a.ogSiteName || undefined,
                        dateText: timeSince(a.publishedAt),
                        body: a.excerpt || a.ogDescription,
                        imageUrl: heroImg || undefined,
                      }}
                    />
                  )}
                </article>
              );
            }

            /* ── Regular community share card (shared FeedCard) ───── */
            const imgUrl = resolveImageUrl(a) || null;
            const sharedByMe = !!currentUserGuid && !!a.sharedBy
              && String(a.sharedBy).toUpperCase() === String(currentUserGuid).toUpperCase();
            const actions: FeedCardMenuAction[] = [];
            if (isSystemAdmin || sharedByMe) {
              actions.push({
                key: 'remove', danger: true,
                label: t('communityDetail.feedRemoveFromCommunity', { name: communityName }),
                onSelect: () => handleRemove(item.moderationGuid),
              });
            }
            if (currentUserGuid && !a.chronicleGuid) {
              actions.push({
                key: 'chronicle',
                label: t('communityDetail.feedShareToChronicle'),
                onSelect: () => setChroniclePickerItem(item),
              });
            }
            if (isSystemAdmin && a.contentType === 'community_share' && !a.chronicleGuid && !isProtectedDomain(item)) {
              actions.push({
                key: 'blacklist', danger: true,
                label: t('communityDetail.feedBlacklistDomain'),
                onSelect: () => handleBlacklistDomain(item),
              });
            }
            return (
              // DEV-1441 — one-shot glow on the freshly shared card while
              // the Voice ribbon is up (newest card = the first post).
              <div
                key={item.moderationGuid}
                className={voiceMoment && itemIdx === 0 ? VOICE_GLOW_CLASS : undefined}
              >
              <FeedCard
                enableComments={isAdult}
                model={{
                  id: item.moderationGuid,
                  authorName: (a.sharedBy && resolveUserName(a.sharedBy))
                    || a.sourceName || a.sourceDomain || a.ogSiteName || t('communityDetail.feedShared'),
                  authorAvatarUrl: (a.sharedBy && resolveUserAvatar(a.sharedBy)) || null,
                  dateText: timeSince(a.publishedAt),
                  embedHtml: a.embedType === 'video' ? a.embedHtml : null,
                  embedType: a.embedType,
                  imageSrc: imgUrl,
                  theaterImageUrl: imgUrl,
                  title: a.title || a.ogTitle || a.canonicalUrl || null,
                  body: a.excerpt || a.ogDescription || a.shareComment || null,
                  anchorType: a.chronicleGuid ? 'chronicle' : (a.newsItemGuid ? 'news_item' : null),
                  anchorId: a.chronicleGuid || a.newsItemGuid || null,
                  onOpen: () => handleItemClick(a),
                  menuActions: actions,
                }}
              />
              </div>
            );
          })}
        </div>
      )}

      {/* Share to Chronicle Picker */}
      {currentUserGuid && (
        <ChroniclePickerModal
          isOpen={chroniclePickerItem !== null}
          onClose={() => setChroniclePickerItem(null)}
          newsItemGuid={chroniclePickerItem?.article?.newsItemGuid || ''}
          articleTitle={chroniclePickerItem?.article?.title || chroniclePickerItem?.article?.ogTitle || ''}
          currentUserGuid={currentUserGuid}
          onShared={(chronicleGuid, chronicleTitle) => {
            setChroniclePickerItem(null);
          }}
        />
      )}
    </div>
  );
};

export default CommunityFeed;
